import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import SMQMainContainer from "../../../components/SMQMainContainer";
import DirectionService from "../../../services/DirectionService";
import UserService from "../../../services/UserService";
import ConstatService from "../../../services/smq/ConstatService";
import ExigenceService from "../../../services/smq/ExigenceService";
import ImportService from "../../../services/smq/ImportService";
import ProcessusService from "../../../services/smq/ProcessusService";
import TriggerService from "../../../services/smq/TriggerService";
import {
  IMPORT_COLUMNS,
  MAX_FILE_SIZE,
  buildUserMappingGroups,
  downloadImportTemplate,
  normalizeValue,
  readImportWorkbook,
  toConstatPayload,
  toCuratifPayload,
  userLabel,
  userOptionLabel,
  validateImportRows,
} from "./importNCUtils";

const emptyReferences = {
  directions: [],
  exigences: [],
  processus: [],
  triggers: [],
  users: [],
};

const responseRows = (payload, label) => {
  if (payload?.status === false || payload?.status === 0 || !Array.isArray(payload?.data)) {
    throw new Error(payload?.message || `${label} a retourné une réponse invalide.`);
  }
  return payload.data;
};

const rowStatus = (row) => {
  if (row.importStatus === "duplicate") {
    return { color: "warning", label: `Doublon de la ligne ${row.duplicateOf}` };
  }
  if (!row.valid) return { color: "error", label: "Invalide" };
  if (row.importStatus === "success") return { color: "success", label: "Importée" };
  if (row.importStatus === "partial") return { color: "warning", label: "Curatif en échec" };
  if (row.importStatus === "failed") return { color: "error", label: "Échec" };
  return { color: "info", label: "Prête" };
};

const ImportNC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [references, setReferences] = useState(emptyReferences);
  const [referencesLoading, setReferencesLoading] = useState(true);
  const [referencesError, setReferencesError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [sourceRows, setSourceRows] = useState([]);
  const [userAssignments, setUserAssignments] = useState({});
  const [rows, setRows] = useState([]);
  const [fileError, setFileError] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [summary, setSummary] = useState(null);
  const [archive, setArchive] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const loadReferences = useCallback(async () => {
    setReferencesLoading(true);
    setReferencesError(null);
    try {
      const [processus, directions, triggers, exigences, users] = await Promise.all([
        ProcessusService.getProcessus(),
        DirectionService.getDirection(),
        TriggerService.getAll(),
        ExigenceService.getAll(),
        UserService.getAllUserActive(),
      ]);
      const loadedReferences = {
        directions: responseRows(directions, "Le service des directions"),
        exigences: responseRows(exigences, "Le service des exigences"),
        processus: responseRows(processus, "Le service des processus"),
        triggers: responseRows(triggers, "Le service des déclencheurs"),
        users: responseRows(users, "Le service des utilisateurs"),
      };
      const missingReference = ["directions", "exigences", "processus", "triggers"].find(
        (key) => loadedReferences[key].length === 0
      );
      if (missingReference) {
        throw new Error("Une liste de références obligatoire est vide.");
      }
      setReferences(loadedReferences);
    } catch (error) {
      setReferences(emptyReferences);
      setReferencesError(
        error?.message || "Impossible de charger les références nécessaires à l’import."
      );
    } finally {
      setReferencesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReferences();
  }, [loadReferences]);

  const invalidRows = useMemo(() => rows.filter((row) => !row.valid), [rows]);
  const invalidDataRows = useMemo(
    () => rows.filter((row) => row.errors.length > row.userIssues.length),
    [rows]
  );
  const userMappingGroups = useMemo(
    () => buildUserMappingGroups(validateImportRows(sourceRows, references, {})),
    [sourceRows, references]
  );
  const pendingUserGroups = useMemo(() => buildUserMappingGroups(rows), [rows]);
  const duplicateRows = useMemo(
    () => rows.filter((row) => row.importStatus === "duplicate"),
    [rows]
  );
  const importableRows = useMemo(
    () => rows.filter((row) => row.valid && !row.duplicateOf),
    [rows]
  );
  const importEnabled =
    Boolean(selectedFile) &&
    importableRows.length > 0 &&
    invalidRows.length === 0 &&
    !importing &&
    !summary;

  const resetFileState = () => {
    setSelectedFile(null);
    setSourceRows([]);
    setUserAssignments({});
    setRows([]);
    setFileError(null);
    setProgress(0);
    setSummary(null);
    setArchive(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const selectFile = async (file) => {
    resetFileState();
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      setFileError("Seuls les fichiers .xlsx sont acceptés.");
      return;
    }
    if (file.size <= 0 || file.size > MAX_FILE_SIZE) {
      setFileError("Le fichier doit avoir une taille maximale de 10 Mo.");
      return;
    }
    if (referencesError || referencesLoading) {
      setFileError("Les références doivent être disponibles avant de lire le fichier.");
      return;
    }

    setParsing(true);
    try {
      const sourceRows = await readImportWorkbook(file);
      setSourceRows(sourceRows);
      setRows(validateImportRows(sourceRows, references, {}));
      setSelectedFile(file);
    } catch (error) {
      setFileError(error?.message || "Impossible de lire le fichier XLSX.");
    } finally {
      setParsing(false);
    }
  };

  const assignUser = (key, userId) => {
    const nextAssignments = { ...userAssignments };
    if (userId === undefined) delete nextAssignments[key];
    else nextAssignments[key] = userId;
    setUserAssignments(nextAssignments);
    setRows(validateImportRows(sourceRows, references, nextAssignments));
  };

  const previewCellValue = (row, column) => {
    if (["constat_user", "curatif_responsable"].includes(column.key)) {
      const userId = row.data[column.key];
      const user = references.users.find(
        (item) => String(item.user_id) === String(userId)
      );
      if (user) return userLabel(user);
      const assignmentKey = normalizeValue(row.raw[column.key]);
      if (
        assignmentKey &&
        Object.prototype.hasOwnProperty.call(userAssignments, assignmentKey) &&
        userAssignments[assignmentKey] === null
      ) {
        return "Non renseigné";
      }
      return String(row.raw[column.key] || "");
    }
    return row.raw[column.key] instanceof Date
      ? row.data[column.key] || ""
      : String(row.raw[column.key] || "");
  };

  const updateRow = (sourceRow, patch) => {
    setRows((current) =>
      current.map((row) => (row.sourceRow === sourceRow ? { ...row, ...patch } : row))
    );
  };

  const handleImport = async () => {
    if (!importEnabled) return;
    setImporting(true);
    setProgress(0);
    setArchive({ status: "pending", message: "Archivage en attente." });

    let imported = 0;
    let failed = 0;
    let curativeFailed = 0;
    let firstConstatId = null;

    for (let index = 0; index < importableRows.length; index += 1) {
      const row = importableRows[index];
      try {
        const response = await ConstatService.createConstat(toConstatPayload(row));
        const constatId = response?.data?.constat_id;
        if (!constatId) throw new Error("Réponse de création de NC invalide.");
        if (!firstConstatId) firstConstatId = constatId;
        imported += 1;

        let status = "success";
        let importError = null;
        if (row.hasCuratif) {
          try {
            await ConstatService.createCuratif({
              constat_id: constatId,
              ...toCuratifPayload(row),
            });
          } catch (error) {
            curativeFailed += 1;
            status = "partial";
            importError = error?.message || "Action curative non enregistrée.";
          }
        }
        updateRow(row.sourceRow, { constatId, importError, importStatus: status });
      } catch (error) {
        failed += 1;
        updateRow(row.sourceRow, {
          importError: error?.message || "Import de la NC impossible.",
          importStatus: "failed",
        });
      } finally {
        setProgress(Math.round(((index + 1) / importableRows.length) * 100));
      }
    }

    setSummary({
      curativeFailed,
      duplicates: duplicateRows.length,
      failed,
      imported,
      total: rows.length,
    });

    if (firstConstatId) {
      setArchive({ status: "uploading", message: "Archivage du fichier en cours…" });
      try {
        const response = await ImportService.archive(selectedFile, firstConstatId);
        setArchive({
          status: "success",
          message: response?.message || "Fichier archivé dans l’historique SMQ.",
        });
      } catch (error) {
        setArchive({
          status: "error",
          message:
            error?.message ||
            "Les NC sont traitées, mais le fichier n’a pas pu être archivé.",
        });
      }
    } else {
      setArchive({
        status: "skipped",
        message: "Aucune NC créée : aucun historique d’import enregistré.",
      });
    }
    setImporting(false);
  };

  const handleTemplateDownload = async () => {
    setFileError(null);
    try {
      await downloadImportTemplate(references);
    } catch (error) {
      setFileError(error?.message || "Impossible de générer le modèle d’import.");
    }
  };

  return (
    <SMQMainContainer title="Importer des non-conformités">
      <Box sx={{ backgroundColor: "#f1f5fb", minHeight: "calc(100vh - 64px)", p: { xs: 2, md: 3 } }}>
        <Paper sx={{ borderRadius: 3, maxWidth: 1500, mx: "auto", p: { xs: 2, md: 3 } }}>
          <Typography component="h2" fontSize={{ xs: 22, md: 28 }} fontWeight={800}>
            Importer plusieurs non-conformités
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.7 }}>
            Importer des non-conformités à partir d’un fichier Excel.
          </Typography>

          <Alert severity="info" sx={{ mt: 2 }}>
            Téléchargez le modèle, complétez une NC par ligne. Pour les utilisateurs,
            saisissez uniquement leur nom complet. Ne modifiez pas les en-têtes du modèle.
          </Alert>

          {referencesError && (
            <Alert
              action={<Button color="inherit" onClick={loadReferences}>Réessayer</Button>}
              severity="error"
              sx={{ mt: 2 }}
            >
              {referencesError}
            </Alert>
          )}

          <Box sx={{ alignItems: "center", display: "flex", flexWrap: "wrap", gap: 1.5, mt: 2.5 }}>
            <Button
              disabled={referencesLoading || Boolean(referencesError) || importing}
              onClick={handleTemplateDownload}
              startIcon={referencesLoading ? <CircularProgress size={17} /> : <CloudDownloadIcon />}
              sx={{ textTransform: "none" }}
              variant="contained"
            >
              Télécharger le modèle
            </Button>
            {selectedFile && !importing && (
              <Button color="inherit" onClick={resetFileState} sx={{ textTransform: "none" }}>
                Retirer le fichier
              </Button>
            )}
          </Box>

          <Box
            onClick={() => !importing && !parsing && fileInputRef.current?.click()}
            onDragEnter={(event) => { event.preventDefault(); setDragActive(true); }}
            onDragLeave={(event) => { event.preventDefault(); setDragActive(false); }}
            onDragOver={(event) => event.preventDefault()}
            onKeyDown={(event) => {
              if (["Enter", " "].includes(event.key) && !importing && !parsing) {
                event.preventDefault();
                fileInputRef.current?.click();
              }
            }}
            onDrop={(event) => {
              event.preventDefault();
              setDragActive(false);
              if (!importing) selectFile(event.dataTransfer.files?.[0]);
            }}
            role="button"
            tabIndex={0}
            sx={{
              alignItems: "center",
              backgroundColor: dragActive ? "#e4f7fa" : "#f8fbff",
              border: `2px dashed ${dragActive ? "#00a6b6" : "#a9bbcf"}`,
              borderRadius: 3,
              cursor: importing ? "default" : "pointer",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              minHeight: 190,
              mt: 2.5,
              p: 3,
              textAlign: "center",
            }}
          >
            <input
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              hidden
              onChange={(event) => selectFile(event.target.files?.[0])}
              ref={fileInputRef}
              type="file"
            />
            {parsing ? <CircularProgress /> : selectedFile ? <InsertDriveFileIcon color="primary" sx={{ fontSize: 48 }} /> : <CloudUploadIcon color="primary" sx={{ fontSize: 52 }} />}
            <Typography fontWeight={800} sx={{ mt: 1 }}>
              {parsing ? "Lecture du fichier…" : selectedFile ? selectedFile.name : "Déposez le fichier ici ou cliquez pour le choisir"}
            </Typography>
            {selectedFile && (
              <Typography color="text.secondary" fontSize={13}>
                {(selectedFile.size / 1024).toFixed(1)} Ko
              </Typography>
            )}
          </Box>

          {fileError && <Alert severity="error" sx={{ mt: 2 }}>{fileError}</Alert>}

          {rows.length > 0 && (
            <Box sx={{ mt: 3 }}>
              {userMappingGroups.length > 0 && (
                <Paper
                  elevation={0}
                  sx={{ backgroundColor: "#f8fbff", border: "1px solid #cbd9e8", mb: 2, p: 2 }}
                >
                  <Typography fontSize={18} fontWeight={800}>
                    Affectation des utilisateurs
                  </Typography>
                  <Typography color="text.secondary" fontSize={13} sx={{ mb: 2, mt: 0.5 }}>
                    Associez chaque nom inconnu ou ambigu. Une affectation s’applique à toutes ses occurrences.
                  </Typography>
                  <Box sx={{ display: "grid", gap: 1.5 }}>
                    {userMappingGroups.map((group) => {
                      const selectedUser = references.users.find(
                        (user) =>
                          String(user.user_id) === String(userAssignments[group.key])
                      ) || null;
                      const ignored =
                        Object.prototype.hasOwnProperty.call(userAssignments, group.key) &&
                        userAssignments[group.key] === null;
                      const roleLabels = group.roles.map((role) =>
                        role === "constat_user" ? "Constaté par" : "Qui agit"
                      );
                      return (
                        <Box
                          key={group.key}
                          sx={{
                            alignItems: { md: "center" },
                            backgroundColor: "white",
                            border: "1px solid #dce5ef",
                            borderRadius: 2,
                            display: "grid",
                            gap: 1.5,
                            gridTemplateColumns: { md: "minmax(240px, 0.8fr) minmax(320px, 1.2fr) auto" },
                            p: 1.5,
                          }}
                        >
                          <Box>
                            <Typography fontWeight={700}>
                              {group.input || "Nom non renseigné"}
                            </Typography>
                            <Typography color="text.secondary" fontSize={12}>
                              {roleLabels.join(" / ")} · {group.count} occurrence(s) · ligne(s) {group.sourceRows.join(", ")}
                            </Typography>
                          </Box>
                          <Autocomplete
                            filterOptions={(options, state) => {
                              const search = normalizeValue(state.inputValue);
                              return options.filter((user) =>
                                normalizeValue(userLabel(user)).includes(search)
                              );
                            }}
                            getOptionLabel={userOptionLabel}
                            isOptionEqualToValue={(option, value) =>
                              String(option.user_id) === String(value.user_id)
                            }
                            onChange={(_, user) => assignUser(group.key, user?.user_id)}
                            options={references.users}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Rechercher par nom"
                                placeholder="Nom complet"
                                size="small"
                              />
                            )}
                            value={selectedUser}
                          />
                          {group.canIgnore && (
                            <Button
                              color="inherit"
                              onClick={() => assignUser(group.key, null)}
                              size="small"
                              sx={{ textTransform: "none" }}
                              variant={ignored ? "contained" : "text"}
                            >
                              Non renseigné
                            </Button>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Paper>
              )}

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
                <Chip label={`${rows.length} ligne(s)`} />
                <Chip color="success" label={`${importableRows.length} à importer`} />
                <Chip color="warning" label={`${duplicateRows.length} doublon(s)`} />
                <Chip color={invalidRows.length ? "error" : "default"} label={`${invalidRows.length} invalide(s)`} />
              </Box>

              {pendingUserGroups.length > 0 && (
                <Alert severity="warning" sx={{ mb: 1.5 }}>
                  Terminez l’affectation des utilisateurs avant de lancer l’import.
                </Alert>
              )}

              {invalidDataRows.length > 0 && (
                <Alert severity="error" sx={{ mb: 1.5 }}>
                  Corrigez les autres erreurs dans Excel, puis rechargez le fichier.
                </Alert>
              )}

              <TableContainer sx={{ border: "1px solid #dce5ef", maxHeight: 500 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 800 }}>Ligne</TableCell>
                      {IMPORT_COLUMNS.map((column) => (
                        <TableCell key={column.key} sx={{ fontWeight: 800, minWidth: 150 }}>
                          {column.label}
                        </TableCell>
                      ))}
                      <TableCell sx={{ fontWeight: 800, minWidth: 210 }}>Contrôle</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => {
                      const status = rowStatus(row);
                      const messages = [...row.errors, row.importError].filter(Boolean);
                      return (
                        <TableRow key={row.sourceRow} sx={{ backgroundColor: !row.valid ? "#fff5f5" : "inherit" }}>
                          <TableCell>{row.sourceRow}</TableCell>
                          {IMPORT_COLUMNS.map((column) => (
                            <TableCell key={column.key} sx={{ maxWidth: 330, whiteSpace: "normal" }}>
                              {previewCellValue(row, column)}
                            </TableCell>
                          ))}
                          <TableCell>
                            <Chip color={status.color} label={status.label} size="small" />
                            {messages.map((message) => (
                              <Typography color="error" fontSize={11} key={message} sx={{ mt: 0.5 }}>
                                {message}
                              </Typography>
                            ))}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {importing && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress value={progress} variant="determinate" />
                  <Typography color="text.secondary" fontSize={13} sx={{ mt: 0.5 }}>
                    Traitement : {progress}%
                  </Typography>
                </Box>
              )}

              {summary && (
                <Alert severity={summary.failed || summary.curativeFailed ? "warning" : "success"} sx={{ mt: 2 }}>
                  {summary.imported} NC importée(s), {summary.duplicates} doublon(s) ignoré(s), {summary.failed} échec(s) NC, {summary.curativeFailed} échec(s) curatif.
                </Alert>
              )}
              {archive && ["success", "error", "skipped"].includes(archive.status) && (
                <Alert severity={archive.status === "success" ? "success" : archive.status === "error" ? "error" : "info"} sx={{ mt: 1.5 }}>
                  {archive.message}
                </Alert>
              )}

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, justifyContent: "flex-end", mt: 2.5 }}>
                {summary && (
                  <Button onClick={() => navigate("/smq_nc/registre")} sx={{ textTransform: "none" }} variant="outlined">
                    Voir le registre
                  </Button>
                )}
                <Button
                  disabled={!importEnabled}
                  onClick={handleImport}
                  startIcon={importing ? <CircularProgress color="inherit" size={17} /> : <CloudUploadIcon />}
                  sx={{ textTransform: "none" }}
                  variant="contained"
                >
                  {importing ? "Import en cours…" : "Importer les NC"}
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      </Box>
    </SMQMainContainer>
  );
};

export default ImportNC;
